import { DateTime } from 'luxon'
import { Location } from '../../../../../api/models'
import { getUserLocale, locationTimeZone } from '../../../../../utils/dateUtils'
import { EngagementClip, RecordingDetails } from '../types'
import { getStreamingUrlWithRetry } from '../../getStreamingUrlWithRetry'
import { useGetStreamingUrlFn } from '../../../../../api/apiQueries'

// Keep clamped seeks strictly inside the media: landing exactly on the end both reads as "jumped to the
// start" on the timeline and, on the FLV path, wedges mpegts (an at/past-end seek flushes its buffer and,
// without an FLV keyframe index, it never reloads - observed in QA on 2026-08-04).
const SEEK_END_GUARD_MS = 250

// Clamp a seek offset to the video element's ACTUAL media duration. A clip's museum window
// (startTime..endTime) includes the postroll, but mediator serves only what was archived - QA has clips
// whose 65s window wraps an 8s file - so timeline clicks can map to offsets past the real footage.
// Duration is NaN until metadata loads (and Infinity for live-ish sources); pass through untouched then.
// offsetSeconds/mediaDurationSeconds stay in seconds (not ms) to match HTMLMediaElement.currentTime/duration,
// the native units of every call site.
export const clampSeekOffsetToMedia = (offsetSeconds: number, mediaDurationSeconds: number): number =>
  Number.isFinite(mediaDurationSeconds) && mediaDurationSeconds > 0
    ? Math.min(offsetSeconds, Math.max(0, mediaDurationSeconds - SEEK_END_GUARD_MS / 1000))
    : offsetSeconds

export const CLIP_ABSOLUTE_TIME_FORMAT = 'hh:mm:ss a ZZZZ'

// The absolute time of a playhead position, as the player controls display it: the PLAYING clip's start
// plus the element's currentTime (which is always relative to that clip's start, never the camera's
// earliest footage). Shown in the location's time zone (the browser's when the location isn't available).
export const formatClipAbsoluteTime = (clipStartTimeMs: number, currentTimeSeconds: number, location: Location | undefined): string =>
  DateTime.fromMillis(clipStartTimeMs, { locale: getUserLocale() })
    .plus({ seconds: currentTimeSeconds })
    .setZone(locationTimeZone(location))
    .toFormat(CLIP_ABSOLUTE_TIME_FORMAT)

export function getRecordingDetails(clip: EngagementClip): RecordingDetails {
  const flvUrl = clip.links['playback/flv']?.href
  if (flvUrl) {
    return { url: flvUrl, provider: 'flv' }
  }
  const hlsUrl = clip.links['playback/hls']?.href
  if (hlsUrl) {
    return { url: hlsUrl, provider: 'hls' }
  }
  // there's a playback/dash but we should never be using that
  throw new Error('No valid playback URL found for recording.')
}

export function createAuthenticatedFlvUrl(mediatorUrl: string): string {
  return `${mediatorUrl}&audioEncoding=AAC`
}

/**
 * This function is puprosedly not catching errors, consumers of this function must handle that themselves.
 */
export async function getHlsStreamingUrl(
  accessToken: string,
  mediatorUrl: string,
  getStreamingUrl: ReturnType<typeof useGetStreamingUrlFn>
): Promise<string | undefined> {
  const { streamingSessionURL } = await getStreamingUrlWithRetry(getStreamingUrl, mediatorUrl, accessToken)
  return streamingSessionURL
}
